import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import LoginScreen from './components/LoginScreen';
import MainScreen from './components/MainScreen';
import { User } from './types';
import api from './services/api';
import websocketService from './services/websocket';
import { clearMailboxAccessTokens } from './services/mailboxAccess';

interface AppState {
  currentScreen: 'login' | 'main';
  user: User | null;
  isLoading: boolean;
  notification: {
    id: string;
    message: string;
    type: 'error' | 'success';
    show: boolean;
    hiding: boolean;
  } | null;
}

function App() {
  const [state, setState] = useState<AppState>({
    currentScreen: 'login',
    user: null,
    isLoading: true,
    notification: null
  });
  const notificationTimersRef = useRef<number[]>([]);

  const normalizeUser = (raw: any): User => {
    // Telegram-логин мог сохранить isAdmin как 0/1, нормализуем до boolean
    return {
      ...raw,
      isAdmin: Boolean(raw?.isAdmin),
      isChild: raw?.isChild == null ? undefined : Boolean(raw?.isChild),
      parentUserId: raw?.parentUserId ?? null
    } as User;
  };

  const isAuthSessionMessage = (message: string) => {
    const text = String(message || '').toLowerCase();
    return (
      text.includes('session expired') ||
      text.includes('token expired') ||
      text.includes('invalid token') ||
      text.includes('access disabled') ||
      text.includes('access denied') ||
      text.includes('unauthorized')
    );
  };

  // We intentionally do not use sessionStorage gating here.
  // A user can become a "child" after login (linked by admin), and UI should update
  // after a simple page refresh / tab focus.
  const profileRefreshRef = useRef<{ userId: number; at: number } | null>(null);
  const profileRefreshInFlightRef = useRef(false);
  const [isScreenSwitching, setIsScreenSwitching] = useState(false);
  const [displayScreen, setDisplayScreen] = useState<'login' | 'main'>(state.currentScreen);

  useEffect(() => {
    // Восстановление сессии должно выполняться только один раз на старте.
    const savedUser = localStorage.getItem('mailspace_user');
    if (savedUser) {
      try {
        const user = normalizeUser(JSON.parse(savedUser));
        setState(prev => ({
          ...prev,
          user,
          currentScreen: 'main',
          isLoading: false
        }));
        return;
      } catch {
        localStorage.removeItem('mailspace_user');
      }
    }
    setState(prev => ({ ...prev, isLoading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasUser = Boolean(state.user);
    if (!hasUser) return;

    // Слушаем изменения в localStorage (для автоматического выхода при 401)
    const handleStorageChange = () => {
      const currentUser = localStorage.getItem('mailspace_user');
      if (!currentUser) {
        setState(prev => {
          if (!prev.user) return prev;
          return {
            ...prev,
            user: null,
            currentScreen: 'login'
          };
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Fallback polling (rare storage-event misses). Keep it lightweight.
    const interval = window.setInterval(() => {
      const currentUser = localStorage.getItem('mailspace_user');
      if (!currentUser) {
        setState(prev => {
          if (!prev.user) return prev;
          return {
            ...prev,
            user: null,
            currentScreen: 'login'
          };
        });
      }
    }, 15000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.clearInterval(interval);
    };
  }, [state.user]);

  useEffect(() => {
    // Refresh /auth/me after (re)login to get isChild/parentUserId from server.
    // This fixes cases where localStorage has an old user object.
    const token = localStorage.getItem('token');
    if (!token) return;
    if (state.currentScreen !== 'main') return;
    if (!state.user) return;
    if (profileRefreshInFlightRef.current) return;
    if (profileRefreshRef.current?.userId === state.user.id) return;

    let cancelled = false;
    profileRefreshInFlightRef.current = true;

    const run = async () => {
      try {
        api.refreshToken();
        const resp = await api.getProfile();
        if (cancelled) return;
        if (resp.success && resp.data?.user) {
          const merged = normalizeUser({ ...state.user, ...resp.data.user });
          localStorage.setItem('mailspace_user', JSON.stringify(merged));
          setState(prev => ({ ...prev, user: merged }));
          profileRefreshRef.current = { userId: merged.id, at: Date.now() };
        }
      } catch {
        // ignore: UI still works with cached user data
      } finally {
        profileRefreshInFlightRef.current = false;
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentScreen, state.user?.id]);

  useEffect(() => {
    // When the tab regains focus, allow a lightweight re-check.
    const onFocus = () => {
      if (state.currentScreen !== 'main') return;
      if (!state.user) return;
      if (!localStorage.getItem('token')) return;
      const last = profileRefreshRef.current?.at || 0;
      if (Date.now() - last < 3000) return; // throttle
      profileRefreshRef.current = null; // allow refresh effect to run again
      setState(prev => ({ ...prev })); // tick
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [state.currentScreen, state.user]);

  useEffect(() => {
    setIsScreenSwitching(true);

    let contentSwapTimer: number | null = null;
    // Безопасно: если user уже null (logout), не держим MainScreen в DOM.
    if (state.currentScreen === 'login') {
      if (!state.user) {
        setDisplayScreen('login');
      } else {
        contentSwapTimer = window.setTimeout(() => {
          setDisplayScreen('login');
        }, 170);
      }
    } else {
      setDisplayScreen('main');
    }

    const timer = window.setTimeout(() => {
      setIsScreenSwitching(false);
    }, 460);

    return () => {
      if (contentSwapTimer) window.clearTimeout(contentSwapTimer);
      window.clearTimeout(timer);
    };
  }, [state.currentScreen, state.user]);

  const handleLogin = (user: User) => {
    const normalized = normalizeUser(user);
    localStorage.setItem('mailspace_user', JSON.stringify(normalized));
    setState(prev => ({
      ...prev,
      user: normalized,
      currentScreen: 'main'
    }));
  };

  const clearLocalSession = () => {
    api.clearAuthStorage();
    clearMailboxAccessTokens();
    websocketService.disconnect();
  };

  const handleLogout = async () => {
    try {
      if (api.getToken()) {
        await api.logout();
      }
    } catch {
      // Even if the server token is already expired, the local session must go away.
    }
    clearLocalSession();
    localStorage.removeItem('mailspace_user');
    setState(prev => ({
      ...prev,
      user: null,
      currentScreen: 'login'
    }));
  };

  useEffect(() => {
    const handleAuthCleared = () => {
      clearNotificationTimers();
      clearLocalSession();
      setDisplayScreen('login');
      setIsScreenSwitching(false);
      setState(prev => ({
        ...prev,
        user: null,
        currentScreen: 'login',
        notification: null
      }));
    };
    window.addEventListener('mailspace-auth-cleared', handleAuthCleared);
    return () => window.removeEventListener('mailspace-auth-cleared', handleAuthCleared);
  }, []);

  const clearNotificationTimers = () => {
    notificationTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    notificationTimersRef.current = [];
  };

  useEffect(() => {
    return () => clearNotificationTimers();
  }, []);

  const showNotification = (message: string, type: 'error' | 'success') => {
    if (type === 'error' && isAuthSessionMessage(message) && !api.getToken()) {
      clearNotificationTimers();
      clearLocalSession();
      setDisplayScreen('login');
      setIsScreenSwitching(false);
      setState(prev => ({
        ...prev,
        user: null,
        currentScreen: 'login',
        notification: null
      }));
      return;
    }

    clearNotificationTimers();
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    setState(prev => ({ ...prev, notification: { id, message, type, show: false, hiding: false } }));

    const showTimer = window.setTimeout(() => {
      setState(prev => {
        if (prev.notification?.id !== id) return prev;
        return { ...prev, notification: { ...prev.notification, show: true } };
      });
    }, 10);

    const hideTimer = window.setTimeout(() => {
      setState(prev => {
        if (prev.notification?.id !== id) return prev;
        return { ...prev, notification: { ...prev.notification, hiding: true, show: false } };
      });
    }, 3500);

    const removeTimer = window.setTimeout(() => {
      setState(prev => {
        if (prev.notification?.id !== id) return prev;
        return { ...prev, notification: null };
      });
    }, 4100);

    notificationTimersRef.current = [showTimer, hideTimer, removeTimer];
  };

  if (state.isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Загрузка Почтальон Печкин...</p>
      </div>
    );
  }

  return (
    <div className="app">
      {state.notification && (
        <div className={`global-notification ${state.notification.type === 'error' ? 'error-toast' : 'success-toast'} ${state.notification.show ? 'show' : ''} ${state.notification.hiding ? 'hiding' : ''}`}>
          {state.notification.message}
        </div>
      )}
      
      <div className={`app-container ${state.currentScreen === 'login' ? 'login-mode' : 'app-mode'} ${isScreenSwitching ? 'screen-switching' : ''}`}>
        {displayScreen === 'main' && state.user ? (
          <MainScreen user={state.user} onLogout={handleLogout} onShowNotification={showNotification} />
        ) : (
          <LoginScreen onLogin={handleLogin} onShowNotification={showNotification} />
        )}
      </div>
    </div>
  );
}

export default App;
